import dayjs from 'dayjs';
import * as api from 'app/shared/api';
import { DivisionPricingYear, isDraftPricingYear } from 'app/shared/api/getYears';
import { OfferingType } from 'app/shared/model/EditPricelist';

export type CalculatorOfferingOption = {
    productId: string;
    name: string;
};

type SchoolLike = {
    id?: string | number;
    code?: string;
    name?: string;
    centreCode?: string;
};

function toOfferingType(productType: string): OfferingType {
    if (productType === 'Add-ons') return 'AddOn';
    return productType as OfferingType;
}

export function toPricingEngineProductCode(
    productIdText: string | undefined | null,
    productType: string,
    productId?: { id?: number | string } | number | string | null
): string | undefined {
    const fromId =
        productId != null && typeof productId === 'object' && 'id' in productId && productId.id != null
            ? `${toOfferingType(productType)}-${productId.id}`
            : productId != null && (typeof productId === 'string' || typeof productId === 'number')
              ? `${toOfferingType(productType)}-${productId}`
              : '';
    const raw = String(productIdText || fromId || '').trim();
    if (!raw) return undefined;
    if (/^[CATXP]\d+$/i.test(raw)) return raw.toUpperCase();

    const num = raw.match(/(\d+)/)?.[1];
    if (!num) return raw;

    const prefixByType: Record<string, string> = {
        Course: 'C',
        Accommodation: 'A',
        Transfer: 'T',
        'Add-ons': 'X',
        AddOn: 'X',
        Package: 'P',
    };
    const prefix = prefixByType[productType] ?? prefixByType[toOfferingType(productType)] ?? '';
    return `${prefix}${num}`;
}

function pickPricingYear(
    years: DivisionPricingYear[],
    dateFrom?: string
): DivisionPricingYear | undefined {
    if (!years.length) return undefined;
    const published = years.filter((y) => !isDraftPricingYear(y));
    const candidates = published.length ? published : years;

    if (dateFrom) {
        const d = dayjs(dateFrom);
        if (d.isValid()) {
            const byRange = candidates.find((y) => {
                if (y.pricingYearStartDate && y.pricingYearEndDate) {
                    const start = dayjs(y.pricingYearStartDate);
                    const end = dayjs(y.pricingYearEndDate);
                    return (
                        start.isValid() &&
                        end.isValid() &&
                        !d.isBefore(start, 'day') &&
                        !d.isAfter(end, 'day')
                    );
                }
                return false;
            });
            if (byRange) return byRange;

            const byCalendarYear = candidates.find((y) => y.year === d.year());
            if (byCalendarYear) return byCalendarYear;
        }
    }

    return [...candidates].sort((a, b) => b.year - a.year)[0];
}

function isAdminStyleCentreId(centreId: string): boolean {
    return /^\d+$/.test(centreId) || /^CN\d+$/i.test(centreId);
}

function toNumericSchoolId(id: string | number | undefined | null): number | undefined {
    const numericId = Number(
        String(id ?? '')
            .replace(/^School-/i, '')
            .trim()
    );
    return Number.isFinite(numericId) && numericId > 0 ? numericId : undefined;
}

function schoolMatchesCode(school: SchoolLike, code: string): boolean {
    const upper = code.toUpperCase();
    const schoolCode = String(school.code ?? '').toUpperCase();
    const schoolId = String(school.id ?? '')
        .replace(/^School-/i, '')
        .toUpperCase();
    const schoolName = String(school.name ?? '').toUpperCase();
    return schoolCode === upper || schoolId === upper || schoolName === upper;
}

async function resolveAdminCentreId(centreId?: string): Promise<string | number | undefined> {
    if (!centreId) return undefined;
    const normalized = String(centreId).trim();
    if (!normalized) return undefined;
    if (isAdminStyleCentreId(normalized)) return normalized;

    const centres = await api.getAllCentres().catch(() => []);
    const upper = normalized.toUpperCase();
    const match = centres.find((c) => {
        const centreCode = String(c.centreCode ?? '').toUpperCase();
        const code = String(c.code ?? '').toUpperCase();
        const id = String(c.id ?? '')
            .replace(/^CN/i, '')
            .toUpperCase();
        const name = String(c.name ?? '').toUpperCase();
        return centreCode === upper || code === upper || id === upper || name === upper;
    });
    return match?.id;
}

async function resolveAdminSchoolIds(
    schoolCodes: string[],
    centreId?: string
): Promise<number[]> {
    const codes = schoolCodes.map((s) => String(s).replace(/^School-/i, '').trim()).filter(Boolean);
    if (!codes.length) return [];

    const adminCentreId = await resolveAdminCentreId(centreId);
    let adminSchools: SchoolLike[] = [];
    let usedSchoolSummaryFallback = false;

    if (adminCentreId != null && String(adminCentreId).trim() !== '') {
        adminSchools = await api.getSchoolsByCentre(adminCentreId).catch(() => []);
    }
    if (!adminSchools.length && centreId && String(centreId) !== String(adminCentreId ?? '')) {
        adminSchools = await api.getSchoolsByCentre(centreId).catch(() => []);
    }
    if (!adminSchools.length) {
        adminSchools = await api.getAllSchools().catch(() => []);
        usedSchoolSummaryFallback = adminSchools.length > 0;
    }

    const ids = new Set<number>();
    const unmatched: string[] = [];
    for (const code of codes) {
        const match = adminSchools.find((s) => schoolMatchesCode(s, code));
        const numericId = toNumericSchoolId(match?.id);
        if (numericId != null) {
            ids.add(numericId);
        } else {
            unmatched.push(code);
        }
    }

    if (unmatched.length && usedSchoolSummaryFallback) {
        const centreUpper = String(centreId ?? '').toUpperCase();
        const candidateIds = adminSchools
            .filter((s) => {
                if (!centreUpper) return false;
                return String(s.centreCode ?? '').toUpperCase() === centreUpper;
            })
            .map((s) => toNumericSchoolId(s.id))
            .filter((id): id is number => id != null);

        if (candidateIds.length) {
            const details = await Promise.all(
                [...new Set(candidateIds)].map((id) => api.getSchoolById(id).catch(() => null))
            );
            for (const code of unmatched) {
                const match = details.find((d) => d != null && schoolMatchesCode(d, code));
                const numericId = toNumericSchoolId(match?.id);
                if (numericId != null) ids.add(numericId);
            }
        }

        for (const code of unmatched) {
            if (!/^\d+$/.test(code)) continue;
            if ([...ids].some((id) => String(id) === code)) continue;
            const byId = await api.getSchoolById(code).catch(() => null);
            const byIdNum = toNumericSchoolId(byId?.id);
            if (byIdNum != null) ids.add(byIdNum);
        }
    } else if (unmatched.length) {
        for (const code of unmatched) {
            if (!/^\d+$/.test(code)) continue;
            const byId = await api.getSchoolById(code).catch(() => null);
            const byIdNum = toNumericSchoolId(byId?.id);
            if (byIdNum != null) ids.add(byIdNum);
        }
    }

    return [...ids];
}

/** Kept for easy rollback; Calculator currently loads offerings from Pricing API only. */
export async function loadAdminOfferingsForCalculator(params: {
    divisionId: number;
    schoolCodes: string[];
    centreId?: string;
    productType: string;
    dateFrom?: string;
}): Promise<CalculatorOfferingOption[]> {
    const { divisionId, schoolCodes, centreId, productType, dateFrom } = params;
    const adminSchoolIds = await resolveAdminSchoolIds(schoolCodes, centreId);
    if (!adminSchoolIds.length) {
        return [];
    }

    const years = await api.getPricingYearsByDivision(divisionId).catch(() => [] as DivisionPricingYear[]);
    const pricingYear = pickPricingYear(years, dateFrom);
    if (!pricingYear?.id) {
        return [];
    }

    const offeringType = toOfferingType(productType);
    const result = await api.GetAllActiveOfferings(
        {
            schoolIds: adminSchoolIds.map((id) => ({ id })),
            pricingYearId: pricingYear.id,
            type: offeringType,
        },
        divisionId
    );

    if (!result.ok || !result.data?.length) {
        return [];
    }

    const merged = new Map<string, CalculatorOfferingOption>();
    for (const offering of result.data as any[]) {
        const name = String(offering?.name ?? '').trim();
        const code = toPricingEngineProductCode(
            offering?.productIdText,
            productType,
            offering?.productId
        );
        if (!code || !name) continue;

        const statusList = Array.isArray(offering?.schoolStatuses) ? offering.schoolStatuses : [];
        const inactiveForAll =
            statusList.length > 0 &&
            statusList.every((s: any) => {
                const status = String(s?.status ?? s?.Status ?? '').toLowerCase();
                return status === 'inactive' || status === 'disabled';
            });
        if (inactiveForAll) continue;

        if (!merged.has(code)) {
            merged.set(code, { productId: code, name });
        }
    }

    return [...merged.values()].sort((a, b) => a.name.localeCompare(b.name));
}

export function weeksFilterForProductType(
    productType: string,
    weeks?: number
): number | undefined {
    if (weeks == null || weeks <= 0) return undefined;
    if (
        productType === 'Add-ons' ||
        productType === 'AddOn' ||
        productType === 'Transfer' ||
        productType === 'Package'
    ) {
        return undefined;
    }
    return weeks;
}